import { ExchangeServerInstanceType } from "client/Api/Email/ExchangeSettings/environmentExchangeSettings";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { IOwaNavigator } from "./common";
import { DefaultOnlineOwaNavigator } from "./DefaultOnlineOwaNavigator";
import { OnPremisesOwaNavigator } from "./OnPremisesOwaNavigator";
import { CustomUrlOnlineOwaNavigator } from "./CustomUrlOnlineOwaNavigator";
import { isNotNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";

function resolveNavigator(options: { ensureInstanceType?: ExchangeServerInstanceType; } = {}): IOwaNavigator {
	const {
		ensureInstanceType
	} = options;

	const { exchange } = userConfiguration();

	const {
		ewsUrl,
		isConfigured,
		exchangeServerInstanceType
	} = exchange;

	if (isNotNil(ensureInstanceType)) {
		if (ensureInstanceType !== exchangeServerInstanceType) {
			throw new Error(`Expected different instance type at this point ${ensureInstanceType}`);
		}
	}

	if (!isConfigured) {
		throw new Error("Exchange is not configured");
	}

	const index: number = ewsUrl.toLowerCase()
		.indexOf("/ews/exchange.asmx");

	if (index === -1) {
		throw new Error("Unknown ews url");
	}

	const owaUrl: string = ewsUrl.substring(0, index);

	switch (exchangeServerInstanceType) {
		case ExchangeServerInstanceType.Office365:
			if (isInCollectionIgnoreCase(
				owaUrl,
				[
					"https://outlook.office365.com",
					"https://outlook.office.com"
				]
			)) {
				return new DefaultOnlineOwaNavigator();
			}

			return new CustomUrlOnlineOwaNavigator(owaUrl + "/owa");
		case ExchangeServerInstanceType.OnPremises:
			return new OnPremisesOwaNavigator(owaUrl + "/owa");
		default:
			never(exchangeServerInstanceType);
	}
}

export function navigateToComposeEmailInOwaOnPremises(): void {
	const navigator: IOwaNavigator = resolveNavigator({
		ensureInstanceType: ExchangeServerInstanceType.OnPremises
	});

	navigator.composeEmailInNewWindow();
}

export function navigateToComposeEmailInOwa({ to, body }: { to?: string | nil; body?: string; } = {}): void {
	const navigator: IOwaNavigator = resolveNavigator({
		ensureInstanceType: ExchangeServerInstanceType.Office365
	});

	navigator.composeEmailInNewWindow({to: to ?? undefined, body});
}

export function navigateToEmailInOwaOnline(id: string, inNewWindow: boolean): void {
	const navigator: IOwaNavigator = resolveNavigator({
		ensureInstanceType: ExchangeServerInstanceType.Office365
	});

	navigator.emailItem({id, inNewWindow});
}

export function navigateToOwa({ inNewWindow }: { inNewWindow: boolean; } = { inNewWindow: true }): void {
	const navigator: IOwaNavigator = resolveNavigator();

	navigator.emailOverview({inNewWindow});
}

export function navigateToComposeAppointmentInOwa(): void {
	const navigator: IOwaNavigator = resolveNavigator();

	navigator.composeAppointmentInNewWindow();
}

export function navigateToOwaCalendar(inNewWindow: boolean): void {
	const navigator: IOwaNavigator = resolveNavigator();

	navigator.calendarOverview({inNewWindow});
}

export function navigateToOwaPeople(): void {
	const navigator: IOwaNavigator = resolveNavigator();

	navigator.peopleOverview({inNewWindow: true});
}

export function navigateToOwaAppointment(id: string, isInNewWindow: boolean): void {
	const navigator: IOwaNavigator = resolveNavigator();

	navigator.appointmentItem({id, inNewWindow: isInNewWindow});
}

function isInCollectionIgnoreCase(
	value: string,
	options: readonly string[]
): boolean {
	return options.some(
		o => o.toUpperCase() === value.toUpperCase()
	);
}